import { useMutation } from "@apollo/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Note3 from "components/Note/Note3";
import { ModernButton } from "components/ui/modernButton";
import { ComposeActions, ComposeSection } from "components/ui/v2";
import { useModal } from "context/modal/modal.provider";
import { gqlMessage } from "gql";
import { ICreateMessageInput } from "gql/Message/mutations";
import { useExperienceGain } from "hooks/useExperienceGain";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { EMessageType, IMessage } from "types/Message.type";
import { IUser } from "types/User.types";
import useSound from "use-sound";
import { checkIfNodeHaveText } from "utils/message";

interface IForm {
  message: string;
  messageLength: number;
}
interface INewMessageTextProps {
  resetMessageInput: () => void;
  usersToMention: IUser[];
  inline?: boolean;
}

const NewMessageText = (props: INewMessageTextProps) => {
  const { resetMessageInput, usersToMention, inline } = props;
  const [playComplete] = useSound("/sounds/feedback-sound.mp3", {
    volume: 0.25
  });
  const { actions: experienceActions } = useExperienceGain();
  const { closeModal } = useModal();
  const { control, handleSubmit, setValue, watch } = useForm<IForm>();
  const { t } = useTranslation("common");
  const [contentEmpty, setContentEmpty] = useState<boolean>(true);

  const [createMessage, { loading: createMessageLoading }] = useMutation<
    { createMessage: IMessage },
    {
      createMessageInput: ICreateMessageInput;
    }
  >(gqlMessage.mutations.CREATE_MESSAGE, {
    onCompleted: () => {
      setValue("message", "");
      resetMessageInput();
      toast.success(t("Mensaje publicado correctamente"));
      playComplete();
      experienceActions.sendMessage();
      closeModal();
    }
  });

  const handlePublish = (data: IForm) => {
    createMessage({
      variables: {
        createMessageInput: {
          type: EMessageType.TEXT,
          text: data?.message
        }
      }
    });
  };

  useEffect(() => {
    const value = watch("message");
    if (!value) {
      setContentEmpty(true);
      return;
    }

    try {
      const blocks = JSON.parse(value);
      setContentEmpty(!checkIfNodeHaveText(blocks));
    } catch {
      setContentEmpty(true);
    }
  }, [watch("message")]);

  if (inline) {
    return (
      <>
        <ComposeSection compact>
          <Controller
            name="message"
            control={control}
            render={({ field }) => (
              <Note3
                {...field}
                compact
                onChange={(e) => field.onChange(e)}
                placeholder={t("Escribe algo… @menciona o pega imágenes")}
                usersToMention={usersToMention}
              />
            )}
          />
        </ComposeSection>
        <ComposeActions
          compact
          onPublish={handleSubmit(handlePublish)}
          loading={createMessageLoading}
          disabled={contentEmpty}
          loadingText={t("Publicando")}
          publishText={t("Publicar")}
          publishVariant="indigo"
        />
      </>
    );
  }

  return (
    <div className="space-y-6 rounded-xl">
      {/* Message Composition Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold">
            {t("Comparte tu mensaje")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate__animated animate__fadeIn relative rounded-md p-2">
            <Controller
              name="message"
              control={control}
              render={({ field }) => (
                <Note3
                  {...field}
                  onChange={(e) => field.onChange(e)}
                  placeholder={t(
                    "Escribe un mensaje, @menciona a alguien o copia y pega imágenes"
                  )}
                  usersToMention={usersToMention}
                />
              )}
            />
          </div>
        </CardContent>
      </Card>

      {/* Action Section */}
      <div className="flex justify-end pt-4">
        <ModernButton
          variant="primary"
          onClick={handleSubmit(handlePublish)}
          loading={createMessageLoading}
          loadingText={t("Publicando")}
          disabled={contentEmpty}
          className="h-12 min-w-[140px] border border-transparent px-8 py-3 text-base font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg active:scale-95"
        >
          {createMessageLoading ? (
            <div className="flex items-center space-x-2">
              <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
              <span>{t("Publicando")}</span>
            </div>
          ) : (
            t("Publicar")
          )}
        </ModernButton>
      </div>
    </div>
  );
};

export default NewMessageText;
